import React, { memo } from 'react';
import Loader from '../Loader/Loader';
import { useImageSize } from 'react-image-size';

interface GridGalleryProps {
    data: { slider: string[]; } | null;
    loading: boolean;
    error: Error | null;
}

const Image = memo((props: { image: string }) => {
    const [dimensions, { loading }] = useImageSize(props.image);

    if (loading) {
        return <Loader />
    }

    if (!dimensions) {
        return (<img src={props.image} alt={`Visualizacion`} />)
    } else if (dimensions?.width > dimensions?.height) {
        return (<img src={props.image} className={'height-full'} alt={`Visualizacion`} />)
    } else {
        return (<img src={props.image} alt={`Visualizacion`} />)
    }
});

Image.displayName = 'Image';

const ImageGroup = memo(({ image, index, type }: { image: string; index: number; type: 'vertical' | 'horizontal' }) => (
    <div className={`gridImage__group gridImage__group--${type}`} key={`${type}-${index}`}>
        <div className="gridImage__group__image">
            <Image image={image} />
        </div>
    </div>
));

ImageGroup.displayName = 'ImageGroup';

const HorizontalGroup = memo(({ image, nextImage, index }: { image: string; nextImage: string; index: number }) => (
    <div className="gridImage__group gridImage__group--horizontal" key={`horizontal-${index}`}>
        <div className="gridImage__group__image">
            <Image image={image} />
        </div>
        <div className="gridImage__group__image">
            <Image image={nextImage} />
        </div>
    </div>
));

HorizontalGroup.displayName = 'HorizontalGroup';

const GridGallery = memo(({ data, loading, error }: GridGalleryProps) => {
    if (loading) return <Loader />;
    if (error) return <div>Error: {error.message}</div>;

    const availableImages = data ? data.slider : [];
    const reducedImages = availableImages.length > 6 ? availableImages.slice(0, 6) : availableImages;
    const totalImages = [...reducedImages];

    return (
        <div className="gridImage">
            <div className="gridImage__container">
                {totalImages.map((image, index) => {
                    const position = index % 3;
                    if (position === 0) {
                        return <ImageGroup image={image} index={index} type="vertical" key={`group-${index}`} />;
                    } else if (position === 1) {
                        return (
                            <React.Fragment key={`fragment-${index}`}>
                                {totalImages[index + 1] ? (
                                    <HorizontalGroup
                                        image={image}
                                        nextImage={totalImages[index + 1]}
                                        index={index}
                                    />
                                ) : (
                                    <ImageGroup image={image} index={index} type="vertical" />
                                )}
                            </React.Fragment>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
});

GridGallery.displayName = 'GridGallery';

export default GridGallery;